import { useState } from 'react';
import { clsx } from 'clsx';
import { getWildShapeOptions, sortBeastsByCR } from '@character-creator/data';
import type { Beast, ChallengeRating } from '@character-creator/types';
import { CR_VALUES } from '@character-creator/types';

interface WildShapeSelectorProps {
  druidLevel: number;
  compact?: boolean;
}

/**
 * Format a CR value for display
 */
function formatCR(cr: ChallengeRating): string {
  if (cr === '0') return 'CR 0';
  return `CR ${cr}`;
}

/**
 * Get CR restrictions text based on druid level
 */
function getRestrictionText(druidLevel: number): string {
  if (druidLevel >= 8) {
    return 'Max CR 1, flying and swimming allowed';
  } else if (druidLevel >= 4) {
    return 'Max CR 1/2, no flying speed';
  } else {
    return 'Max CR 1/4, no flying or swimming speed';
  }
}

/**
 * Format speed for display
 */
function formatSpeed(speed: Beast['speed']): string {
  const parts: string[] = [];
  if (speed.walk) parts.push(`${speed.walk} ft.`);
  if (speed.fly) parts.push(`fly ${speed.fly} ft.`);
  if (speed.swim) parts.push(`swim ${speed.swim} ft.`);
  if (speed.climb) parts.push(`climb ${speed.climb} ft.`);
  if (speed.burrow) parts.push(`burrow ${speed.burrow} ft.`);
  return parts.join(', ');
}

/**
 * Calculate ability modifier
 */
function getModifier(score: number): string {
  const mod = Math.floor((score - 10) / 2);
  return mod >= 0 ? `+${mod}` : `${mod}`;
}

export function WildShapeSelector({ druidLevel, compact = false }: WildShapeSelectorProps) {
  const [selectedBeast, setSelectedBeast] = useState<Beast | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);

  // Druids get Wild Shape at level 2
  if (druidLevel < 2) {
    return (
      <div className="text-xs text-text-muted">
        Wild Shape available at level 2
      </div>
    );
  }

  const availableBeasts = sortBeastsByCR(getWildShapeOptions(druidLevel));

  // Group beasts by CR
  const beastsByCR = availableBeasts.reduce(
    (acc, beast) => {
      const cr = beast.challengeRating;
      if (!acc[cr]) acc[cr] = [];
      acc[cr].push(beast);
      return acc;
    },
    {} as Record<ChallengeRating, Beast[]>
  );

  // Get sorted CR keys
  const crKeys = Object.keys(beastsByCR).sort(
    (a, b) => CR_VALUES[a as ChallengeRating] - CR_VALUES[b as ChallengeRating]
  ) as ChallengeRating[];

  // Compact view
  if (compact && !isExpanded) {
    return (
      <div className="text-xs text-text-muted">
        <button
          type="button"
          onClick={() => setIsExpanded(true)}
          className="text-accent-gold hover:underline"
        >
          View Wild Shape forms ({availableBeasts.length} available)
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h5 className="text-sm font-medium text-text-primary">Wild Shape Forms</h5>
          <p className="text-xs text-text-muted">{getRestrictionText(druidLevel)}</p>
        </div>
        {compact && (
          <button
            type="button"
            onClick={() => {
              setIsExpanded(false);
              setSelectedBeast(null);
            }}
            className="text-xs text-text-muted hover:text-text-secondary"
          >
            Collapse
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Beast List */}
        <div className="space-y-3">
          {crKeys.map((cr) => (
            <div key={cr}>
              <h6 className="text-xs font-medium text-text-muted mb-1">{formatCR(cr)}</h6>
              <div className="grid grid-cols-2 gap-1">
                {beastsByCR[cr].map((beast) => (
                  <button
                    key={beast.id}
                    type="button"
                    onClick={() => setSelectedBeast(selectedBeast?.id === beast.id ? null : beast)}
                    className={clsx(
                      'text-left px-2 py-1.5 rounded text-xs transition-colors',
                      selectedBeast?.id === beast.id
                        ? 'bg-accent-gold/20 text-accent-gold'
                        : 'bg-background-secondary text-text-secondary hover:bg-background-elevated hover:text-text-primary'
                    )}
                  >
                    {beast.name}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Selected Beast Stats */}
        {selectedBeast && (
          <div className="bg-background-secondary rounded-lg p-3 text-xs space-y-3">
            <div className="flex items-start justify-between">
              <div>
                <h6 className="font-semibold text-text-primary text-sm">{selectedBeast.name}</h6>
                <p className="text-text-muted capitalize">
                  {selectedBeast.size} beast
                </p>
              </div>
              <span className="text-accent-gold font-medium">{formatCR(selectedBeast.challengeRating)}</span>
            </div>

            {/* Core Stats */}
            <div className="grid grid-cols-3 gap-2">
              <div className="bg-background-elevated rounded p-2 text-center">
                <div className="text-text-muted text-[10px] uppercase">AC</div>
                <div className="text-text-primary font-bold">{selectedBeast.armorClass}</div>
              </div>
              <div className="bg-background-elevated rounded p-2 text-center">
                <div className="text-text-muted text-[10px] uppercase">HP</div>
                <div className="text-text-primary font-bold">{selectedBeast.hitPoints.average}</div>
              </div>
              <div className="bg-background-elevated rounded p-2 text-center">
                <div className="text-text-muted text-[10px] uppercase">Speed</div>
                <div className="text-text-primary font-bold text-[10px]">{formatSpeed(selectedBeast.speed)}</div>
              </div>
            </div>

            {/* Ability Scores */}
            <div className="grid grid-cols-6 gap-1 text-center">
              {(['strength', 'dexterity', 'constitution', 'intelligence', 'wisdom', 'charisma'] as const).map((ability) => (
                <div key={ability} className="bg-background-elevated rounded p-1">
                  <div className="text-text-muted text-[9px] uppercase">{ability.slice(0, 3)}</div>
                  <div className="text-text-primary font-medium text-[10px]">
                    {selectedBeast.abilityScores[ability]}
                  </div>
                  <div className="text-text-muted text-[9px]">
                    ({getModifier(selectedBeast.abilityScores[ability])})
                  </div>
                </div>
              ))}
            </div>

            {/* Senses */}
            {selectedBeast.senses && selectedBeast.senses.length > 0 && (
              <div>
                <span className="text-text-muted">Senses: </span>
                <span className="text-text-secondary">{selectedBeast.senses.join(', ')}</span>
              </div>
            )}

            {/* Traits */}
            {selectedBeast.traits && selectedBeast.traits.length > 0 && (
              <div className="space-y-1">
                <div className="font-medium text-text-primary">Traits</div>
                {selectedBeast.traits.map((trait) => (
                  <div key={trait.name} className="pl-2 border-l border-background-elevated">
                    <span className="font-medium text-text-secondary">{trait.name}. </span>
                    <span className="text-text-muted">{trait.description}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Actions */}
            {selectedBeast.actions && selectedBeast.actions.length > 0 && (
              <div className="space-y-1">
                <div className="font-medium text-text-primary">Actions</div>
                {selectedBeast.actions.map((action) => (
                  <div key={action.name} className="pl-2 border-l border-background-elevated">
                    <span className="font-medium text-text-secondary">{action.name}. </span>
                    <span className="text-text-muted">{action.description}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Placeholder when no beast selected */}
        {!selectedBeast && (
          <div className="bg-background-secondary rounded-lg p-4 flex items-center justify-center">
            <p className="text-text-muted text-xs text-center">
              Select a beast to view its stat block
            </p>
          </div>
        )}
      </div>

      {/* Wild Shape Info */}
      <div className="text-xs text-text-muted bg-background-secondary rounded p-2">
        <p>
          <strong>Wild Shape:</strong> You can use your action to transform into a beast you have seen before.
          You can use this feature twice per short or long rest. Your druid level determines the beasts you can transform into.
        </p>
      </div>
    </div>
  );
}
